// Treasury → Currencies (SM-444). The operator's view of the three
// fixed platform currencies: RED, GREEN and GOLD.
//
// Three things this page is deliberately NOT:
//
//   - It is not a currency manager. There is no create action and no
//     delete action, anywhere, at any permission level, because there is
//     no such endpoint and there never will be: "exactly three fixed
//     currencies" is an epic acceptance criterion, not a default. The
//     page must not even imply otherwise, so there is no "+ Add" affordance
//     and no row menu.
//   - It is not the authority on what a user may do. The ledger enforces
//     capability; this renders it. Where the two could disagree,
//     `ceilingDisagreement()` shows the disagreement rather than picking
//     a side.
//   - Since SM-504 it is not where the crypto RAIL is configured. The
//     mode → currency → asset+currency hierarchy lives in Crypto Wallets
//     → Deposit & withdrawal; this page reads its resolved answer back
//     and links there.
//
//     What it IS again, since SM-514 rd2, is the one editor for the
//     LEDGER's own operator switch — a different rung, owned by a
//     different service, which the rail console renders as its own
//     LEDGER level and cannot set. SM-504 removed the control here on
//     the "two editors, one question" argument; that argument holds, and
//     this is the one editor. Without it the console could name the
//     blocker and do nothing about it, which is how RED deposits stayed
//     shut with every switch an operator could reach turned on.
//   - It is not a place where money moves. Balances, funding and
//     conversion liquidity live on Treasury → Conversion pools.
//
// What it IS: the one screen that explains, per currency, what is
// configured, what is locked forever, and — separately — which of the
// four layers is currently saying no.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Card, CardBody } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { NotFoundSurface } from "@/components/ui/NotFoundSurface";
import { CurrencyCard, readiness } from "@/components/currency/CurrencyCard";
import { POLICY_ORDER } from "@/components/currency/policy";
import { CurrencyEditDialog } from "@/components/currency/CurrencyEditDialog";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import {
  currencyKeys,
  fetchConversionRoutes,
  fetchCurrencyRegistry,
  fetchPlayerCurrencyReadEnabled,
  updateCurrency,
} from "@/lib/currency-queries";
import { Perm } from "@/lib/perms";
import { useMyPerms } from "@/hooks/useMyPerms";
import { toast } from "@/lib/toast";
import type { Direction } from "@/components/currency/policy";
import type { CurrencyCode } from "@/lib/currency";
import type { CurrencyInfo, UpdateCurrencyInput } from "@/lib/currency-types";
import { currencySettingsEnabled } from "@/lib/featureFlags";
import {
  fetchCryptoAssets,
  fetchCryptoFlags,
  fetchCryptoRates,
  fetchRailSnapshot,
  railKeys,
  walletKeys,
} from "@/lib/wallet-queries";

/**
 * The switch-flip confirmation copy (SM-514 rd2).
 *
 * Whole strings per direction and per target state, exactly like
 * CurrencyCard's DIRECTION_LABEL and RAIL_BADGE and for the same reason:
 * a frame with the direction interpolated reaches a translator with an
 * English word substituted into the middle of a translated sentence.
 * Four titles, four labels and four bodies is more text than one frame
 * and is the only shape a translator can actually work with.
 */
const SWITCH_CONFIRM_TITLE: Record<Direction, Record<"on" | "off", string>> = {
  deposit: { on: "Turn deposits on?", off: "Turn deposits off?" },
  withdraw: { on: "Turn withdrawals on?", off: "Turn withdrawals off?" },
};

const SWITCH_CONFIRM_LABEL: Record<Direction, Record<"on" | "off", string>> = {
  deposit: { on: "Turn deposits on", off: "Turn deposits off" },
  withdraw: { on: "Turn withdrawals on", off: "Turn withdrawals off" },
};

const SWITCH_CONFIRM_BODY: Record<Direction, Record<"on" | "off", string>> = {
  deposit: {
    on: "Every user will be able to start a deposit in this currency, as far as the crypto rail configuration and the rollout flags allow. This switch permits; it does not open a rail on its own.",
    off: "No user will be able to start a new deposit in this currency, whatever the crypto rail configuration says. Deposits already in flight still complete — this gate is on initiation only.",
  },
  withdraw: {
    on: "Every user will be able to start a withdrawal in this currency, as far as the crypto rail configuration allows. This switch permits; it does not open a rail on its own.",
    off: "No user will be able to start a new withdrawal in this currency, whatever the crypto rail configuration says. Withdrawals already in flight still complete — this gate is on initiation only.",
  },
};

/**
 * The confirmation body, with the currency named in its OWN text node.
 *
 * Interpolating the code into the sentence would hand the runtime
 * translator a frame to substitute into; keeping it beside the sentence
 * keeps both translatable and puts the currency where an operator who
 * opened the wrong card will see it.
 */
function switchConfirmBody(code: string, direction: Direction, next: boolean) {
  return (
    <>
      <span className="font-semibold">{code}</span>
      {" — "}
      {SWITCH_CONFIRM_BODY[direction][next ? "on" : "off"]}
    </>
  );
}

export const Route = createFileRoute("/_app/currencies")({
  component: CurrenciesPage,
});

function CurrenciesPage() {
  // G1: this flag gates INITIATION of a new page only. Nothing in flight
  // lives here — no order, no deposit, no pool movement — so hiding the
  // whole route when it is dark strands nothing.
  if (!currencySettingsEnabled()) return <NotFoundSurface />;
  return <CurrenciesView />;
}

function CurrenciesView() {
  const qc = useQueryClient();
  const perms = useMyPerms();
  // While permissions load, show the action: the server enforces
  // CURRENCY_CONFIG in both hops regardless, and a button that appears
  // late is worse than one that is refused.
  const canEdit = !perms.loaded || perms.isSuperAdmin || perms.has(Perm.CurrencyConfig);

  // The OPERATOR read (SM-485): values, limits and the audit pair, and
  // deliberately not F1-gated — the switches must be inspectable while
  // the player-facing flag is still dark.
  const registry = useQuery({
    queryKey: currencyKeys.registry(),
    queryFn: fetchCurrencyRegistry,
  });

  // F1's state, probed separately now that the values no longer come
  // from the flag-gated route. It is one of SM-444's four SEPARATE
  // indicators, so it has to be observed rather than inferred.
  const playerRead = useQuery({
    queryKey: currencyKeys.playerReadFlag(),
    queryFn: fetchPlayerCurrencyReadEnabled,
  });
  const flagOn = playerRead.data === true;

  // The stored terms of every conversion route, so the card can report the
  // rate and the fee AC 1 asks for instead of pointing at another page.
  //
  // Its own permission and its own posture: CONVERSION_VIEW is a different
  // grant from CURRENCY_VIEW, and an operator who holds the second but not the
  // first must still get this page in full — so a refusal or a failure here
  // degrades to "not readable" beside the route rather than to a banner. The
  // registry read above keeps its loud failure; that one IS this page.
  const canViewConversions = perms.isSuperAdmin || perms.has(Perm.ConversionView);
  const routes = useQuery({
    queryKey: currencyKeys.routes(),
    queryFn: fetchConversionRoutes,
    enabled: perms.loaded && canViewConversions,
    retry: false,
  });

  const [editing, setEditing] = useState<CurrencyInfo | null>(null);
  // The pending switch flip, held until the operator confirms it. Both
  // directions are confirmed, not only the off one: turning a rail ON
  // opens it to every user of the platform, which is at least as
  // consequential as closing it, and an "are you sure" that only appears
  // for one of them teaches an operator that the other is routine.
  const [flip, setFlip] = useState<{
    row: CurrencyInfo;
    direction: Direction;
    next: boolean;
  } | null>(null);
  const save = useMutation({
    mutationFn: (v: { code: string; patch: UpdateCurrencyInput }) =>
      updateCurrency(v.code, v.patch),
    onSuccess: (_row, v) => {
      // Invalidate rather than write the response in: the registry is
      // read by other surfaces too, and the server's row is the truth.
      void qc.invalidateQueries({ queryKey: currencyKeys.registry() });
      setEditing(null);
      toast.success(`${v.code} settings saved`);
    },
  });

  // The ledger switch flip. Deliberately the SAME endpoint and the same
  // "send only what changed" shape as the presentation dialog — one
  // field, nothing else re-asserted — so the audit row records a switch
  // change and not an operator re-saving eleven values they never looked
  // at.
  //
  // The rail snapshot is invalidated alongside the registry: the crypto
  // console resolves this switch as its LEDGER rung, so a flip that did
  // not refresh it would leave the two consoles disagreeing again, which
  // is the whole bug. crypto-wallet-service caches the ledger read for
  // 15s, so the refetched snapshot can still be one beat behind; the
  // toast says the change is saved, not that every surface has caught up.
  const setSwitch = useMutation({
    mutationFn: (v: { code: string; patch: UpdateCurrencyInput }) =>
      updateCurrency(v.code, v.patch),
    onSuccess: (_row, v) => {
      void qc.invalidateQueries({ queryKey: currencyKeys.registry() });
      void qc.invalidateQueries({ queryKey: railKeys.snapshot() });
      setFlip(null);
      toast.success(`${v.code} availability saved`);
    },
  });

  // Provider readiness for RED deposits is not a field anyone publishes;
  // it is a conclusion drawn from the crypto rail's own configuration.
  // These reads are the same ones Wallet Ops → Configuration shows, so
  // the two pages can never disagree about whether a rail exists.
  const flags = useQuery({ queryKey: walletKeys.flags(), queryFn: fetchCryptoFlags });
  const assets = useQuery({ queryKey: walletKeys.assets(), queryFn: () => fetchCryptoAssets() });
  const depositAssets = (assets.data ?? []).filter((a) => a.deposit_enabled);
  const rates = useQuery({
    // One rates read per deposit-enabled asset; the rate is where
    // SM-416 records which currency a deposit credits.
    queryKey: [...walletKeys.all, "rates-by-credited-currency", depositAssets.map((a) => a.asset_id)],
    queryFn: async () =>
      (await Promise.all(depositAssets.map((a) => fetchCryptoRates(a.asset_id)))).flat(),
    enabled: assets.isSuccess,
  });

  // The crypto rail's own configuration hierarchy (SM-504), read once
  // for the whole page. Its answer is a LAYER on each card, never
  // merged with the ledger's switch: the two are owned by different
  // services and a merged light would send an operator to the wrong
  // console.
  const rails = useQuery({ queryKey: railKeys.snapshot(), queryFn: fetchRailSnapshot });
  const railFor = (code: string, direction: "DEPOSIT" | "WITHDRAW") => ({
    resolved: rails.data?.resolved?.[`${code}|${direction}`],
    loading: rails.isLoading,
    failed: rails.isError,
  });

  const provider = readiness(flags.data, depositAssets, rates.data, {
    loading: flags.isLoading || assets.isLoading || rates.isLoading,
    failed: flags.isError || assets.isError || rates.isError,
  });

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="font-display text-2xl tracking-tight">Currencies</h1>
        <p className="mt-1 max-w-[85ch] text-sm text-muted">
          The three fixed platform currencies. Codes, roles, capability ceilings
          and conversion routes are permanent — they cannot be added to, removed
          or widened. Presentation and the ledger&apos;s own deposit and
          withdrawal switches are set here; the crypto rail hierarchy behind
          them is configured under Crypto Wallets and shown below read-only.
        </p>
        <Link
          to="/wallet-ops/config/rails"
          className="text-xs underline underline-offset-2 hover:text-t-1"
        >
          Open Crypto Wallets → Deposit &amp; withdrawal
        </Link>
      </header>

      {registry.isLoading ? (
        <Skeleton className="h-64" />
      ) : registry.isError ? (
        <Card>
          <CardBody>
            <p className="text-sm text-no">
              The currency registry could not be read. Capability states below
              cannot be trusted until it comes back.
            </p>
          </CardBody>
        </Card>
      ) : (
        <>
          {!flagOn && <PlayerReadDarkNotice />}
          <div className="space-y-4">
            {POLICY_ORDER.map((code) => (
              <CurrencyCard
                key={code}
                code={code}
                row={registry.data?.find((c) => c.code === code)}
                flagOn={flagOn}
                provider={provider}
                routes={routes.data}
                onEdit={canEdit ? setEditing : undefined}
                rail={{
                  deposit: railFor(code, "DEPOSIT"),
                  withdraw: railFor(code, "WITHDRAW"),
                }}
                onSetSwitch={
                  canEdit
                    ? (row, direction, next) => {
                        setSwitch.reset();
                        setFlip({ row, direction, next });
                      }
                    : undefined
                }
                switchSaving={
                  setSwitch.isPending && flip?.row.code === code ? flip.direction : null
                }
              />
            ))}
          </div>
        </>
      )}

      {flip && (
        <ConfirmModal
          open
          title={SWITCH_CONFIRM_TITLE[flip.direction][flip.next ? "on" : "off"]}
          description={switchConfirmBody(flip.row.code, flip.direction, flip.next)}
          confirmLabel={SWITCH_CONFIRM_LABEL[flip.direction][flip.next ? "on" : "off"]}
          tone={flip.next ? "primary" : "danger"}
          loading={setSwitch.isPending}
          error={setSwitch.error ? (setSwitch.error as Error).message : null}
          onConfirm={() =>
            setSwitch.mutate({
              code: flip.row.code,
              patch:
                flip.direction === "deposit"
                  ? { deposit_enabled: flip.next }
                  : { withdraw_enabled: flip.next },
            })
          }
          onCancel={() => {
            setSwitch.reset();
            setFlip(null);
          }}
        />
      )}

      {editing && (
        <CurrencyEditDialog
          code={editing.code as CurrencyCode}
          row={editing}
          open
          saving={save.isPending}
          error={save.error ? (save.error as Error).message : null}
          onSave={(patch) => save.mutate({ code: editing.code, patch })}
          onCancel={() => {
            save.reset();
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

// --- notices -------------------------------------------------------

/**
 * F1 is dark upstream — the PLAYER read is not mounted.
 *
 * Since SM-485 that no longer stops this page working: the operator
 * read is a different route and is deliberately not flag-gated, so
 * every value below is live and every switch is settable. What the
 * flag being dark DOES mean is that no player can see any of it yet,
 * which is a different fact and is worth saying separately — it is one
 * of the four indicators SM-444 requires, and folding it into
 * "unavailable" is what made this page read-only-looking before.
 */
function PlayerReadDarkNotice() {
  return (
    <Card className="border-warn/40">
      <CardBody className="space-y-1">
        <p className="text-sm font-semibold text-warn">
          Not yet visible to players — feature flag F1 is off
        </p>
        <p className="max-w-[85ch] text-xs text-t-3">
          The player-facing currency read is not mounted, which is how this
          console detects that the currency-reads flag is still dark. The
          settings below are live and editable — operator switches are meant to
          be set before a rail is turned on — but nothing here reaches a user
          until the flag is enabled.
        </p>
      </CardBody>
    </Card>
  );
}
